import { useState } from 'react';
import { useStore } from '../../context/StoreContext';
import { useToast } from '../../context/ToastContext';
import { Plus, X, ArrowUpRight, Trash2, Loader2 } from 'lucide-react';

interface AdminCategoriesProps {
  onNavigate: (path: string) => void;
}

export function AdminCategories({ onNavigate }: AdminCategoriesProps) {
  const { categories, refreshCategories } = useStore();
  const { showToast } = useToast();
  const [showModal, setShowModal] = useState(false);
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [description, setDescription] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [sortOrder, setSortOrder] = useState('1');
  const [submitting, setSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const handleNameChange = (val: string) => {
    setName(val);
    if (!slug || slug === name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')) {
      setSlug(val.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, ''));
    }
  };

  const handleCreateCategory = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name || !slug) {
      showToast('error', 'Campos obrigatórios', 'Preencha o nome e o slug da categoria.');
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch('/api/admin/categories', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name,
          slug,
          description,
          image_url: imageUrl,
          sort_order: parseInt(sortOrder, 10) || 0,
        }),
      });

      const data = (await res.json()) as any;
      if (!res.ok) throw new Error(data.error || 'Erro ao criar categoria.');

      showToast('success', 'Categoria criada', 'A categoria foi registrada no banco D1.');
      setShowModal(false);
      setName('');
      setSlug('');
      setDescription('');
      setImageUrl('');
      await refreshCategories();
    } catch (err: unknown) {
      showToast('error', 'Erro', err instanceof Error ? err.message : 'Falha ao salvar categoria.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteCategory = async (catId: string, catName: string) => {
    if (!window.confirm(`Deseja realmente excluir a categoria "${catName}"? Os produtos vinculados serão preservados.`)) {
      return;
    }

    setDeletingId(catId);
    try {
      const res = await fetch(`/api/admin/categories/${catId}`, {
        method: 'DELETE',
      });
      const data = (await res.json()) as any;
      if (!res.ok) throw new Error(data.error || 'Erro ao excluir categoria.');

      showToast('success', 'Categoria excluída', `A categoria "${catName}" foi removida.`);
      await refreshCategories();
    } catch (err: unknown) {
      showToast('error', 'Erro ao excluir', err instanceof Error ? err.message : 'Falha ao excluir categoria.');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-border-subtle pb-6">
        <div>
          <span className="text-[10px] uppercase tracking-[0.2em] font-semibold text-primary">
            Estrutura Taxonômica
          </span>
          <h1 className="text-2xl sm:text-3xl font-serif font-normal text-ink mt-0.5">
            Categorias & Coleções ({categories.length})
          </h1>
        </div>

        <button
          onClick={() => setShowModal(true)}
          className="bg-ink text-ink-inverted px-4 py-2.5 rounded-xs text-xs uppercase tracking-wider font-medium hover:bg-primary transition-colors flex items-center gap-1.5 shadow-subtle self-start sm:self-auto"
        >
          <Plus className="w-4 h-4" /> Criar Categoria
        </button>
      </div>

      {/* Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {categories.map((cat) => (
          <div
            key={cat.id}
            className="bg-surface border border-border-subtle rounded-xs overflow-hidden shadow-subtle flex flex-col"
          >
            <div className="aspect-[16/9] bg-background-alt overflow-hidden">
              <img
                src={
                  cat.image_url ||
                  'https://images.unsplash.com/photo-1610701596007-11502861dcfa?auto=format&fit=crop&w=600&q=80'
                }
                alt={cat.name}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="p-5 flex-1 flex flex-col justify-between">
              <div>
                <div className="flex items-center justify-between">
                  <h3 className="font-serif text-lg font-medium text-ink">{cat.name}</h3>
                  <span className="font-mono text-xs text-ink-muted">
                    {cat.product_count || 0} peças
                  </span>
                </div>
                <p className="font-mono text-[11px] text-ink-muted mt-0.5">/categoria/{cat.slug}</p>
                {cat.description && (
                  <p className="text-xs text-ink-muted mt-2 line-clamp-2 leading-relaxed">
                    {cat.description}
                  </p>
                )}
              </div>

              <div className="mt-4 pt-3 border-t border-border-subtle flex items-center justify-between">
                <button
                  onClick={() => onNavigate(`/categoria/${cat.slug}`)}
                  className="text-xs text-primary font-medium hover:underline flex items-center gap-1"
                >
                  Ver no site <ArrowUpRight className="w-3.5 h-3.5" />
                </button>

                <button
                  onClick={() => handleDeleteCategory(cat.id, cat.name)}
                  disabled={deletingId === cat.id}
                  className="text-xs text-ink-muted hover:text-red-600 transition-colors flex items-center gap-1 px-2 py-1 rounded-xs hover:bg-red-50 disabled:opacity-50"
                  title="Excluir categoria"
                >
                  {deletingId === cat.id ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Trash2 className="w-3.5 h-3.5" />
                  )}
                  <span>Excluir</span>
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 bg-ink/50 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-surface w-full max-w-lg rounded-xs shadow-elevated border border-border-subtle p-6 space-y-6 animate-fadeIn">
            <div className="flex items-center justify-between border-b border-border-subtle pb-4">
              <h3 className="font-serif text-xl font-medium text-ink">Adicionar Categoria</h3>
              <button onClick={() => setShowModal(false)} className="p-1 text-ink-muted hover:text-ink">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleCreateCategory} className="space-y-4 text-xs">
              <div>
                <label className="font-semibold text-ink block mb-1">Nome da Categoria *</label>
                <input
                  type="text"
                  required
                  value={name}
                  onChange={(e) => handleNameChange(e.target.value)}
                  placeholder="Ex: Objetos em Bronze & Metal"
                  className="w-full bg-background border border-border-subtle px-3 py-2 rounded-xs focus:outline-none focus:border-primary"
                />
              </div>

              <div>
                <label className="font-semibold text-ink block mb-1">Slug URL *</label>
                <input
                  type="text"
                  required
                  value={slug}
                  onChange={(e) => setSlug(e.target.value)}
                  placeholder="objetos-em-bronze"
                  className="w-full bg-background border border-border-subtle px-3 py-2 rounded-xs focus:outline-none focus:border-primary font-mono text-[11px]"
                />
              </div>

              <div>
                <label className="font-semibold text-ink block mb-1">Descrição</label>
                <textarea
                  rows={2}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Texto curatorial sobre a categoria"
                  className="w-full bg-background border border-border-subtle p-3 rounded-xs focus:outline-none focus:border-primary"
                />
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-2">
                  <label className="font-semibold text-ink block mb-1">URL da Imagem de Capa</label>
                  <input
                    type="url"
                    value={imageUrl}
                    onChange={(e) => setImageUrl(e.target.value)}
                    placeholder="https://images.unsplash.com/..."
                    className="w-full bg-background border border-border-subtle px-3 py-2 rounded-xs focus:outline-none focus:border-primary"
                  />
                </div>
                <div>
                  <label className="font-semibold text-ink block mb-1">Ordem</label>
                  <input
                    type="number"
                    value={sortOrder}
                    onChange={(e) => setSortOrder(e.target.value)}
                    className="w-full bg-background border border-border-subtle px-3 py-2 rounded-xs focus:outline-none focus:border-primary font-mono"
                  />
                </div>
              </div>

              <div className="pt-4 border-t border-border-subtle flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 border border-border-subtle rounded-xs hover:border-ink/40 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="bg-ink text-ink-inverted px-6 py-2 rounded-xs uppercase tracking-wider font-medium hover:bg-primary transition-colors disabled:opacity-40"
                >
                  {submitting ? 'Salvando...' : 'Criar Categoria'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
